import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { RealtimeService } from '@shared/services/realtime.service';
import { NotificationService } from '@shared/services/notification.service';
import { NotificationsHttpService } from '@shared/services/notifications-http.service';
import { NotificationsRefreshService } from '@shared/services/notifications-refresh.service';
import { MathNotificationToastComponent } from '@shared/components/math-notification-toast/math-notification-toast.component';

@Injectable({
  providedIn: 'root',
})
export class RealtimeNotificationsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeService = inject(RealtimeService);
  private readonly nzNotificationService = inject(NzNotificationService);
  private readonly notificationService = inject(NotificationService);
  private readonly notificationsHttpService = inject(NotificationsHttpService);
  private readonly notificationsRefreshService = inject(
    NotificationsRefreshService,
  );

  private readonly instantToastIds = new Map<string, string>();

  public start(
    toastTemplateRef: MathNotificationToastComponent['templateRef'],
  ): void {
    this.trackRealtimeNotifications(toastTemplateRef);
    this.trackRealtimeTextMessages();
  }

  private trackRealtimeNotifications(
    toastTemplateRef: MathNotificationToastComponent['templateRef'],
  ): void {
    this.realtimeService
      .getNotifications()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => {
        if (!payload.isInstant) return;

        const toastRef = this.nzNotificationService.template(toastTemplateRef, {
          nzData: payload,
          nzDuration: 9999999,
        });

        this.instantToastIds.set(payload.notificationId, toastRef.messageId);

        toastRef.onClose
          ?.pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.instantToastIds.delete(payload.notificationId);
            this.notificationsHttpService
              .readNotifications([payload.notificationId])
              .pipe(take(1), takeUntilDestroyed(this.destroyRef))
              .subscribe(() =>
                this.notificationsRefreshService.notifyChanged(),
              );
          });
      });
  }

  private trackRealtimeTextMessages(): void {
    this.realtimeService
      .getTextMessages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ text }) => {
        this.notificationService.showInfoMessage(text);
      });
  }
}
